import React, {useState, useEffect, useContext} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {useFormContext} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckDouble, faHistory} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {SettingsContext} from "../../contexts/settings.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";

// Utils
import {isMobile} from "../../utils/responsive.js";
import {exists, prettyDateInUserTimezone} from "../../utils/helpers.js";
import {defaultUnacceptableParameterPrompt} from "../../utils/builder.js";

// Components
import InputTextArea from "./InputTextArea.js";
import InputError from "./InputError.js";
import InputCheck from "./InputCheck.js";
import InputSelect from "./InputSelect.js";
import Help from "../Help.js";
import FieldLinkHelp from "./FieldLinkHelp.js";

// Style
import {pad, radius, shadow} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {bp} from "../../style/components/breakpoints.js";
import {flex, z} from "../../style/components/mixins.js";
import {
  Abbr,
  Button,
  CloseButton,
  FormFieldWrapper,
  Inline,
  Label,
  Pill,
  Text
} from "../../style/components/general.js";

const InputRange = ({
  testId = "input-range",
  name,
  label,
  degree = "Decimal",
  precision = 2,
  prompt,
  placeholder,
  options,
  disabled = false,
  units = "",
  aMin,
  aMax,
  adjustMin = false,
  rangeMin = 0,
  rangeMax = 1,
  hasARange = false,
  rangeException = "",
  hasSetPoint = false,
  setLow,
  setHigh,
  overrideWidth,
  innerRef,
  previousRead,
  qualifier = false,
  allowInfinite = false,
  condition,
  tag,
  shared = false,
  linkedAcrossStates = false,
  required = true
}) => {
  const isMounted = useMountedState();

  const {settings} = useContext(SettingsContext);

  const [mobile, setMobile] = useState(false);
  const [prevVisible, setPrevVisible] = useState(false);

  const {
    watch,
    register,
    setValue,
    formState: {errors}
  } = useFormContext();

  const maxValue = watch(name);
  const minValue = watch(`${name}Min`);
  const qualifierEnabled = watch(`${name}_qualifierEnabled`);
  const infinityEnabled = watch(`${name}_infinite`);

  useEffect(() => {
    if (isMounted()) setMobile(isMobile());
  }, [isMounted]);

  const evaluateRange = (low, high) =>
    Number.parseFloat(maxValue) < Number.parseFloat(low) ||
    Number.parseFloat(maxValue) > Number.parseFloat(high);

  const getStep = () => 1 / 10 ** precision;

  const handleMaxChange = e => {
    const parsedValue = Number(e.target.value);
    const parsedMax = Number(minValue) + 1;
    setValue(name, parsedValue >= parsedMax ? parsedValue : parsedMax);
  };

  const handleMinChange = e => {
    const parsedValue = Number(e.target.value);
    const parsedMin = Number(maxValue) - 1;
    setValue(`${name}Min`, parsedValue <= parsedMin ? parsedValue : parsedMin);
  };

  return (
    <>
      <FormFieldWrapper data-testid={testId}>
        <Inline>
          {label && (
            <Label htmlFor={name} bold inline>
              {label.toUpperCase()}
              {required && <span>*</span>}
              {tag && (
                <Pill quiet>
                  <Abbr title={tag}>{tag}</Abbr>
                </Pill>
              )}
              <FieldLinkHelp shared={shared} linkedAcrossStates={linkedAcrossStates} />
              {condition && (
                <Help icon={<FontAwesomeIcon icon={faCheckDouble} />}>{condition}</Help>
              )}
            </Label>
          )}

          {previousRead && previousRead.value && (
            <DetailIcon type="button" onClick={() => setPrevVisible(!prevVisible)} mobile={mobile}>
              <Detail visible={prevVisible}>
                Previous Read:&nbsp;{previousRead.value}&nbsp;{units}&nbsp;on&nbsp;
                {prettyDateInUserTimezone(previousRead.date, settings.timezone, "MMM D YYYY")}
              </Detail>
              <FontAwesomeIcon icon={faHistory} />
            </DetailIcon>
          )}
        </Inline>

        {prompt && <Prompt>{prompt}</Prompt>}

        <Inline>
          {qualifier && <InputCheck hidden name={`${name}_qualifierEnabled`} disabled={disabled} />}
          {qualifier && !qualifierEnabled && (
            <StyledButton
              type="button"
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                setValue(`${name}_qualifierEnabled`, true);
              }}>
              Qualifier
            </StyledButton>
          )}
          {qualifier && qualifierEnabled && (
            <>
              <StyledCloseButton
                data-testid="close-button"
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  setValue(`${name}_qualifier`, null);
                  setValue(`${name}_qualifierEnabled`, false);
                }}
              />
              <SelectWrapper>
                <InputSelect
                  name={`${name}_qualifier`}
                  placeholder="Select..."
                  options={["<", ">", "≤", "≥", "ND", "DNQ", "EST"]}
                  disabled={disabled}
                />
              </SelectWrapper>
            </>
          )}

          <StyledText>
            {exists(minValue) ? options[minValue].label || options[minValue] : ""}
          </StyledText>
          <InputWrapper>
            <RangeInput
              id={name}
              data-testid={`${testId}-input`}
              ref={innerRef}
              type="range"
              placeholder={placeholder}
              disabled={disabled || infinityEnabled}
              min={options ? 0 : rangeMin}
              max={options ? options.length - 1 : rangeMax}
              step={degree === "Number" || options ? 1 : getStep()}
              size="10"
              overrideWidth={overrideWidth}
              onWheel={e => e.target.blur()} // disable scroll increment
              list={options ? "options-list" : undefined}
              {...register(name, {required: required, onChange: handleMaxChange})}
            />

            {/* Don't allow overlap */}
            {adjustMin && (
              <RangeInputMin
                id={`${name}Min`}
                data-testid={`${testId}-input`}
                ref={innerRef}
                type="range"
                placeholder={placeholder}
                disabled={disabled || infinityEnabled}
                min={options ? 0 : rangeMin}
                max={options ? options.length - 1 : rangeMax}
                step={degree === "Number" || options ? 1 : getStep()}
                size="10"
                overrideWidth={overrideWidth}
                onWheel={e => e.target.blur()} // disable scroll increment
                list={options ? "options-list" : undefined}
                {...register(`${name}Min`, {required: required, onChange: handleMinChange})}
              />
            )}

            <Track />
          </InputWrapper>

          {options ? (
            <StyledText>
              {exists(maxValue) ? options[maxValue]?.label || options[maxValue] : ""}
            </StyledText>
          ) : (
            <StyledText>
              {!infinityEnabled ? maxValue : "∞"} {units}
            </StyledText>
          )}

          {hasSetPoint && (
            <Info>
              Set&nbsp;Point:&nbsp;{setLow}&nbsp;-&nbsp;{setHigh}
            </Info>
          )}
        </Inline>

        {allowInfinite && (
          <InputCheck name={`${name}_infinite`} disabled={disabled}>
            Value is Infinite
          </InputCheck>
        )}

        <InputError errors={errors} name={name} />
      </FormFieldWrapper>
      {hasARange && evaluateRange(aMin, aMax) && (
        <InputTextArea
          name={`${name}_comment`}
          label={rangeException || defaultUnacceptableParameterPrompt}
          required
          maxLength={1000}
          disabled={disabled}
        />
      )}
    </>
  );
};

InputRange.propTypes = {
  testId: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  prompt: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.any),
  required: PropTypes.bool,
  degree: PropTypes.string,
  precision: PropTypes.number,
  disabled: PropTypes.bool,
  units: PropTypes.string,
  adjustMin: PropTypes.bool,
  aMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  aMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasARange: PropTypes.bool,
  rangeMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rangeMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rangeException: PropTypes.string,
  hasSetPoint: PropTypes.bool,
  setLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.instanceOf(Element)})
  ]),
  overrideWidth: PropTypes.string,
  previousRead: PropTypes.objectOf(PropTypes.any),
  qualifier: PropTypes.bool,
  allowInfinite: PropTypes.bool,
  condition: PropTypes.string,
  tag: PropTypes.string,
  shared: PropTypes.bool,
  linkedAcrossStates: PropTypes.bool
};

// Style Overrides
const Prompt = styled(Text)`
  margin-top: 2px;
  margin-bottom: ${pad / 2}px;
`;

const Info = styled.span`
  color: ${({theme}) => theme.secondary};
  margin-left: ${pad}px;
  ${voice.quiet};

  ${bp(3)} {
    ${voice.normal};
  }
`;

const InputWrapper = styled.div`
  ${flex("row", "nowrap", "start", "center")}
  gap: ${pad}px;
  position: relative;
`;

const Detail = styled.span`
  ${voice.quiet};
  position: absolute;
  left: 0;
  bottom: 25px;
  color: ${({theme}) => theme.primary};
  background: ${({theme}) => theme.secondary};
  border-radius: 5px;
  padding: ${pad / 4}px ${pad}px;
  transition: all ease 0.2s;
  box-shadow: ${shadow};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;

  ${({visible}) =>
    visible &&
    css`
      opacity: 1;
      visibility: visible;
    `}
`;

const DetailIcon = styled.button`
  ${voice.quiet};
  margin-left: ${pad / 2}px;
  width: min-content;
  color: ${({theme}) => theme.primary};
  background-color: transparent;

  svg {
    fill: ${({theme}) => theme.primary};
  }

  ${({mobile}) =>
    !mobile &&
    css`
      &:hover {
        ${Detail} {
          opacity: 1;
          visibility: visible;
        }
      }
    `}
`;

const RangeInput = styled.input`
  width: 100%;
  min-width: 200px;
  pointer-events: none;
  appearance: none;
  background: none;
  border: 0;
  height: 30px;
  margin: 0;
  z-index: ${z("above")};

  &::-webkit-slider-thumb {
    pointer-events: all;
    appearance: none;
    background: ${({theme}) => theme.secondary};
    border-radius: ${radius};
    width: 10px;
    height: 15px;
    cursor: pointer;
  }
`;

const RangeInputMin = styled(RangeInput)`
  position: absolute;
  left: 0;

  &::-webkit-slider-thumb {
    background: ${({theme}) => theme.primary};
  }
`;

const Track = styled.div`
  position: absolute;
  height: 2px;
  background: ${({theme}) => theme.secondary};
  border-radius: 3px;
  top: 45%;
  left: 0;
  width: 100%;
`;

const StyledCloseButton = styled(CloseButton)`
  margin-right: ${pad}px;
  margin-left: 0;
`;

const StyledButton = styled(Button)`
  margin-right: ${pad}px;
  padding: ${pad}px;
  ${voice.quiet};
`;

const SelectWrapper = styled.div`
  width: 100px;
  margin-right: ${pad}px;
  margin-top: -${pad}px;
`;

const StyledText = styled(Text)`
  ${voice.small};
`;

export default InputRange;
